import { safeDivide } from 'src/utils/app';
import { ComparisonPeriod, WeekRange } from 'src/utils/Hooks';
import {
  TrafficSummaryResponse,
  MarketShareCalculationResult,
  MarketShareValues,
  LastValuesResult
} from './types';

/**
 * Calculates market share values from client and market data
 * 
 * @param clientData - Client-specific data response
 * @param marketData - Total market data response
 * @param metricField - The field to use for the metric value
 * @param marketShareField - Optional field to use for market total
 * @param shouldFilterByCorrespondingWeeks - Whether to filter by corresponding weeks for YTD comparison
 * @param comparisonWeekIdsToInclude - Week IDs to include when filtering
 * @param isComparison - Whether this is comparison period data
 */
export const calculateMarketShare = (
  clientData: TrafficSummaryResponse | undefined,
  marketData: TrafficSummaryResponse | undefined,
  metricField: string,
  marketShareField?: string,
  shouldFilterByCorrespondingWeeks: boolean = false,
  comparisonWeekIdsToInclude: number[] = [],
  isComparison: boolean = false
): MarketShareCalculationResult => {
  if (!clientData || !marketData) {
    return {
      marketShareValue: 0,
      clientValue: 0,
      marketValue: 0
    };
  }

  // Check if we have records (week-by-week data)
  if (clientData.records && marketData.records) {
    let totalClientValue = 0;
    let totalMarketValue = 0;
    let ytdClientValue = 0;
    let ytdMarketValue = 0;

    // Create a map for easier lookup of client data by weekId
    // Preserve null/undefined values to properly identify missing data
    const clientDataByWeekId = new Map(
      clientData.records.map((record) => [record.weekId, record.value])
    );

    // Use API-calculated comparison week IDs for accurate filtering
    const weeksToUse = comparisonWeekIdsToInclude;

    const weeklyMarketShare = marketData.records
      .sort((a, b) => a.weekId - b.weekId)
      .map((record) => {
        const { weekId, value: marketValue } = record;
        const clientValue = clientDataByWeekId.get(weekId);

        // If either value is null or undefined, return null for the share
        if (clientValue === null || clientValue === undefined || marketValue === null || marketValue === undefined) {
          return {
            weekId,
            value: null as number | null
          };
        }

        const marketShareValue = safeDivide(clientValue, marketValue);

        totalClientValue += clientValue;
        totalMarketValue += marketValue;
        if (weeksToUse.includes(weekId)) {
          ytdClientValue += clientValue;
          ytdMarketValue += marketValue;
        }

        return {
          weekId,
          value: marketShareValue
        };
      });

    // Calculate overall market share
    const overallMarketShare =
      shouldFilterByCorrespondingWeeks && isComparison
        ? safeDivide(ytdClientValue, ytdMarketValue)
        : safeDivide(totalClientValue, totalMarketValue);

    return {
      marketShareValue: overallMarketShare,
      clientValue: totalClientValue,
      marketValue: totalMarketValue,
      weeklyMarketShare
    };
  }

  // Fallback to old format (direct property access)
  const fieldToUse = metricField || clientData.metricType || '';
  const marketFieldToUse = marketShareField || fieldToUse;
  const clientValue = clientData[fieldToUse] || 0;
  const marketValue = marketData[marketFieldToUse] || 0;
  const marketShareValue = safeDivide(clientValue, marketValue);

  return {
    marketShareValue,
    clientValue,
    marketValue
  };
};

/**
 * Calculates market share values for both primary and comparison periods
 */
export const calculateMarketShareValues = (
  showMarketShare: boolean,
  primaryData: TrafficSummaryResponse | undefined,
  marketPrimaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  marketComparisonData: TrafficSummaryResponse | undefined,
  metricFieldToUse: string,
  marketShareField: string | undefined,
  skipComparison: boolean,
  comparisonWeekIdsForMainPeriod: number[],
  isComparisonDataCustom: boolean | undefined,
  comparisonPeriod: string,
  weekRange: string
): MarketShareValues => {
  if (!showMarketShare) {
    return {
      primary: { marketShareValue: 0, clientValue: 0, marketValue: 0 },
      comparison: { marketShareValue: 0, clientValue: 0, marketValue: 0 }
    };
  }

  // For YTD comparison with prior year, we need to filter the data correctly
  const shouldFilterByCorrespondingWeeks =
    !isComparisonDataCustom &&
    comparisonPeriod === ComparisonPeriod.PRIOR_YEAR &&
    weekRange === WeekRange.YTD;

  return {
    primary: calculateMarketShare(
      primaryData,
      marketPrimaryData,
      metricFieldToUse,
      marketShareField,
      shouldFilterByCorrespondingWeeks,
      [], // Primary doesn't need week filtering
      false
    ),
    comparison: skipComparison
      ? { marketShareValue: 0, clientValue: 0, marketValue: 0 }
      : calculateMarketShare(
          comparisonData,
          marketComparisonData,
          metricFieldToUse,
          marketShareField,
          shouldFilterByCorrespondingWeeks,
          comparisonWeekIdsForMainPeriod,
          true
        )
  };
};

/**
 * Calculates the total metric value from a response
 * 
 * @param response - The API response containing records or direct values
 * @param metricFieldToUse - The field name to use for the metric value
 * @param metricType - The metric type for fallback field access
 * @param isComparison - Whether this is comparison period data
 * @param comparisonPeriod - The comparison period type
 * @param weekRange - The selected week range
 * @param isComparisonDataCustom - Whether comparison data is custom/shifted
 * @param comparisonWeekIdsForMainPeriod - Week IDs for filtering comparison data
 */
export const calculateMetricTotal = (
  response: TrafficSummaryResponse | undefined,
  metricFieldToUse: string,
  metricType: string,
  isComparison: boolean = false,
  comparisonPeriod?: string,
  weekRange?: string,
  isComparisonDataCustom?: boolean,
  comparisonWeekIdsForMainPeriod: number[] = []
): number => {
  if (!response) {
    return 0;
  }

  // For YTD comparison with prior year, we need to filter the data correctly
  const shouldFilterByCorrespondingWeeks =
    isComparison &&
    !isComparisonDataCustom &&
    comparisonPeriod === ComparisonPeriod.PRIOR_YEAR &&
    weekRange === WeekRange.YTD;

  // Check if the response has records array (new API format)
  if (response.records && Array.isArray(response.records)) {
    const comparisonWeekIdsToInclude = shouldFilterByCorrespondingWeeks
      ? comparisonWeekIdsForMainPeriod
      : null;

    // Filter and sum up all values from records
    return response.records
      .filter(
        (record) =>
          !shouldFilterByCorrespondingWeeks ||
          (comparisonWeekIdsToInclude && comparisonWeekIdsToInclude.includes(record.weekId))
      )
      .reduce((sum, record) => sum + (record.value || 0), 0);
  }

  // Fallback to old format (direct property access)
  return response[metricFieldToUse || metricType] || 0;
};

// ============ HELPER FUNCTIONS FOR LAST VALUE CALCULATIONS ============

type RecordWithValue = { weekId: number; value: number };

/**
 * Checks if records array has data
 */
const hasRecords = (data: TrafficSummaryResponse | undefined): data is TrafficSummaryResponse & { records: RecordWithValue[] } =>
  Boolean(data?.records && data.records.length > 0);

/**
 * Gets the last record from an array (by highest weekId)
 */
const getLastRecord = (records: RecordWithValue[]): RecordWithValue =>
  [...records].sort((a, b) => b.weekId - a.weekId)[0];

/**
 * Gets the last value from records, or 0 if none
 */
const getLastValueFromRecords = (records: RecordWithValue[] | undefined): number =>
  records?.length ? getLastRecord(records).value ?? 0 : 0;

/**
 * Gets the last weekId from records, or 0 if none
 */
const getLastWeekIdFromRecords = (records: RecordWithValue[] | undefined): number =>
  records?.length ? getLastRecord(records).weekId : 0;

/**
 * Finds a record by weekId, returns value or undefined
 */
const findValueByWeekId = (records: RecordWithValue[] | undefined, weekId: number): number | undefined =>
  records?.find((r) => r.weekId === weekId)?.value;

/**
 * Maps a primary week ID to its corresponding comparison week ID
 */
const mapToComparisonWeekId = (
  primaryWeekId: number,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): number | undefined => {
  const index = weekIdsInRange.indexOf(primaryWeekId);
  return index >= 0 ? comparisonWeekIdsForMainPeriod[index] : undefined;
};

/**
 * Gets the comparison value for a given primary week ID
 * Uses a priority chain: mapped week -> fallback to last record
 */
const getComparisonValue = (
  primaryWeekId: number,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[],
  comparisonRecords: RecordWithValue[] | undefined
): number => {
  if (!comparisonRecords?.length) return 0;

  // Try to find the mapped comparison week
  const targetWeekId = mapToComparisonWeekId(primaryWeekId, weekIdsInRange, comparisonWeekIdsForMainPeriod);
  if (targetWeekId !== undefined) {
    const value = findValueByWeekId(comparisonRecords, targetWeekId);
    if (value !== undefined) return value;
  }

  return 0;
};

// ============ LAST VALUE CALCULATION FUNCTIONS ============

/**
 * Gets the last primary value for market share mode
 */
const getLastPrimaryMarketShareValue = (marketShareValues: MarketShareValues): number => {
  const weeklyShare = marketShareValues.primary.weeklyMarketShare;
  
  // Priority: last weekly share value -> overall market share value -> 0
  if (weeklyShare?.length) {
    return weeklyShare[weeklyShare.length - 1].value;
  }
  return marketShareValues.primary.marketShareValue || 0;
};

/**
 * Gets the last secondary value for market share mode
 */
const getLastSecondaryMarketShareValue = (
  primaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  marketShareValues: MarketShareValues,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): number => {
  const comparisonWeeklyShare = marketShareValues.comparison.weeklyMarketShare;

  // If no primary records, just use the last comparison value
  if (!hasRecords(primaryData)) {
    return getLastValueFromRecords(comparisonData?.records);
  }

  const primaryWeekId = getLastWeekIdFromRecords(primaryData.records);
  const targetWeekId = mapToComparisonWeekId(primaryWeekId, weekIdsInRange, comparisonWeekIdsForMainPeriod);

  // Try weekly market share data first
  if (targetWeekId !== undefined && comparisonWeeklyShare?.length) {
    const match = findValueByWeekId(comparisonWeeklyShare, targetWeekId);
    if (match !== undefined) return match;
  }

  // Fallback to comparison records
  return getComparisonValue(primaryWeekId, weekIdsInRange, comparisonWeekIdsForMainPeriod, comparisonData?.records);
};

/**
 * Calculates last values for market share data
 */
const calculateLastValuesForMarketShare = (
  primaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  marketShareValues: MarketShareValues,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): LastValuesResult => ({
  lastPrimaryValue: getLastPrimaryMarketShareValue(marketShareValues),
  lastSecondaryValue: getLastSecondaryMarketShareValue(
    primaryData,
    comparisonData,
    marketShareValues,
    weekIdsInRange,
    comparisonWeekIdsForMainPeriod
  )
});

/**
 * Gets the last primary value for regular (non-market share) mode
 */
const getLastPrimaryRegularValue = (
  primaryData: TrafficSummaryResponse | undefined,
  metricFieldToUse: string,
  metricType: string
): number => {
  // Priority: last record value -> direct field access -> 0
  if (hasRecords(primaryData)) {
    return getLastValueFromRecords(primaryData.records);
  }
  return primaryData?.[metricFieldToUse || metricType] ?? 0;
};

/**
 * Gets the last secondary value for regular (non-market share) mode
 */
const getLastSecondaryRegularValue = (
  primaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): number => {
  // If no comparison records, return 0
  if (!hasRecords(comparisonData)) return 0;

  // If we have primary records, try to find the corresponding comparison value
  if (hasRecords(primaryData)) {
    const primaryWeekId = getLastWeekIdFromRecords(primaryData.records);
    return getComparisonValue(primaryWeekId, weekIdsInRange, comparisonWeekIdsForMainPeriod, comparisonData.records);
  }

  // Fallback to last comparison value
  return 0;
};

/**
 * Calculates last values for regular (non-market share) data
 */
const calculateLastValuesForRegular = (
  primaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  metricFieldToUse: string,
  metricType: string,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): LastValuesResult => ({
  lastPrimaryValue: getLastPrimaryRegularValue(primaryData, metricFieldToUse, metricType),
  lastSecondaryValue: getLastSecondaryRegularValue(primaryData, comparisonData, weekIdsInRange, comparisonWeekIdsForMainPeriod)
});

/**
 * Calculates last values for both primary and comparison data
 * 
 * This replaces the complex nested if-else logic with a clean function
 */
export const calculateLastValues = (
  primaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  showMarketShare: boolean,
  marketShareValues: MarketShareValues,
  metricFieldToUse: string,
  metricType: string,
  weekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): LastValuesResult => {
  if (showMarketShare) {
    return calculateLastValuesForMarketShare(
      primaryData,
      comparisonData,
      marketShareValues,
      weekIdsInRange,
      comparisonWeekIdsForMainPeriod
    );
  }

  return calculateLastValuesForRegular(
    primaryData,
    comparisonData,
    metricFieldToUse,
    metricType,
    weekIdsInRange,
    comparisonWeekIdsForMainPeriod
  );
};

/**
 * Calculates the count of overlapping comparison weeks for average rate calculation
 */
export const getOverlappedComparisonCount = (
  skipComparison: boolean,
  weekRange: string,
  comparisonPeriod: string,
  comparisonData: TrafficSummaryResponse | undefined,
  comparisonWeekIdsInRange: number[],
  comparisonWeekIdsForMainPeriod: number[]
): number => {
  if (skipComparison) {
    return 0;
  }

  const isYtdPriorYear =
    weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

  const comparisonWeeks =
    (comparisonData?.records?.map((r) => r.weekId) as number[]) ?? comparisonWeekIdsInRange;

  if (!isYtdPriorYear) {
    return comparisonWeeks.length;
  }

  // Use API-calculated comparison week IDs for accurate filtering
  const comparisonWeekIdsSet = new Set(comparisonWeekIdsForMainPeriod);

  let count = 0;
  for (const w of comparisonWeeks) {
    if (comparisonWeekIdsSet.has(w)) count++;
  }
  return count;
};
